/**
 * Authentication Service
 *
 * Handles all authentication-related API calls
 */

import api from './api';
import { extractData, extractMessage } from '../utils/apiHelpers';

const TOKEN_STORAGE_KEY = 'authToken';

export const authService = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @param {string} userData.email - Email address
   * @param {string} userData.password - Password
   * @param {string} userData.firstName - First name
   * @param {string} userData.lastName - Last name
   * @param {boolean} userData.marketingConsent - Marketing consent
   * @returns {Promise<Object>} User and token
   */
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', {
        email: userData.email,
        password: userData.password,
        firstName: userData.firstName,
        lastName: userData.lastName,
        acceptsMarketing: userData.marketingConsent || false
      });

      // Extract data from ApiResponse wrapper
      const data = extractData(response);

      // Store token (backend returns: { token, expiresIn, user })
      if (data && data.token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
      }

      return {
        success: true,
        user: data.user,
        message: extractMessage(response)
      };
    } catch (error) {
      console.error('[AuthService] Register error:', error);
      throw error;
    }
  },

  /**
   * Login user
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - Email address
   * @param {string} credentials.password - Password
   * @returns {Promise<Object>} User and token
   */
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', {
        email: credentials.email,
        password: credentials.password
      });

      const data = extractData(response);

      // Store token
      if (data && data.token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
      }

      return {
        success: true,
        user: data.user,
        message: extractMessage(response)
      };
    } catch (error) {
      console.error('[AuthService] Login error:', error);
      throw error;
    }
  },

  /**
   * Logout user
   * @returns {Promise<Object>} Logout response
   */
  logout: async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem(TOKEN_STORAGE_KEY);

      return { success: true };
    } catch (error) {
      console.error('[AuthService] Logout error:', error);
      // Clear token even if API call fails
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      throw error;
    }
  },

  /**
   * Get current user
   * @returns {Promise<Object>} Current user data
   */
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      const data = extractData(response);

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('[AuthService] Failed to get current user:', error.message);
      // Don't clear token here - let the API interceptor or AuthContext handle it
      // This prevents race conditions and centralizes token management
      throw error;
    }
  },

  /**
   * Login with Google OAuth
   * @param {string} idToken - Google ID token
   * @returns {Promise<Object>} User and token
   */
  loginWithGoogle: async (idToken) => {
    try {
      const response = await api.post('/auth/google', { idToken });
      const data = extractData(response);

      // Store token
      if (data && data.token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
      }

      return {
        success: true,
        user: data.user,
        message: extractMessage(response)
      };
    } catch (error) {
      console.error('[AuthService] Google login error:', error);
      throw error;
    }
  },

  /**
   * Check if JWT token is expired
   * @returns {boolean} True if token is expired or invalid
   */
  isTokenExpired: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (!token) {
      return true;
    }

    try {
      // JWT format: header.payload.signature
      const payload = token.split('.')[1];
      const decodedPayload = JSON.parse(atob(payload));

      // Check expiration (exp is in seconds, Date.now() is in ms)
      const expirationTime = decodedPayload.exp * 1000;
      const currentTime = Date.now();

      // Add 60 second buffer (consider expired 1 min before actual expiration)
      const bufferTime = 60 * 1000;

      const isExpired = currentTime >= (expirationTime - bufferTime);

      if (isExpired) {
        console.warn('[AuthService] Token expired at:', new Date(expirationTime));
      }

      return isExpired;
    } catch (error) {
      console.error('[AuthService] Error parsing token:', error);
      // If we can't parse it, consider it invalid
      return true;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user is authenticated
   */
  isAuthenticated: () => {
    const hasToken = !!localStorage.getItem(TOKEN_STORAGE_KEY);

    if (!hasToken) {
      return false;
    }

    // Check if token is expired
    if (authService.isTokenExpired()) {
      console.warn('[AuthService] Token exists but is expired');
      authService.clearToken();
      return false;
    }

    return true;
  },

  /**
   * Get stored token
   * @returns {string|null} JWT token or null
   */
  getToken: () => {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  },

  /**
   * Clear token (logout locally)
   */
  clearToken: () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  },

  /**
   * Request password reset
   * @param {string} email - User's email address
   * @returns {Promise<Object>} Response with success message
   */
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return {
        success: true,
        message: extractMessage(response)
      };
    } catch (error) {
      console.error('[AuthService] Forgot password error:', error);
      throw error;
    }
  },

  /**
   * Validate password reset token
   * @param {string} token - Password reset token
   * @returns {Promise<Object>} Response with token validity and masked email
   */
  validateResetToken: async (token) => {
    try {
      const response = await api.get(`/auth/reset-password?token=${token}`);
      const data = extractData(response);
      return {
        success: true,
        email: data.email,
        expiresAt: data.expiresAt
      };
    } catch (error) {
      console.error('[AuthService] Validate reset token error:', error);
      throw error;
    }
  },

  /**
   * Reset password with token
   * @param {string} token - Password reset token
   * @param {string} password - New password
   * @param {string} confirmPassword - Password confirmation
   * @returns {Promise<Object>} Response with success message
   */
  resetPassword: async (token, password, confirmPassword) => {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        password,
        confirmPassword
      });
      return {
        success: true,
        message: extractMessage(response)
      };
    } catch (error) {
      console.error('[AuthService] Reset password error:', error);
      throw error;
    }
  },

  /**
   * Verify email with token
   * @param {string} token - Email verification token
   * @returns {Promise<Object>} Response with verification status
   */
  verifyEmail: async (token) => {
    try {
      const response = await api.get(`/auth/verify-email?token=${token}`);
      const data = extractData(response);
      return {
        success: true,
        email: data.email,
        verified: data.verified,
        message: extractMessage(response)
      };
    } catch (error) {
      console.error('[AuthService] Verify email error:', error);
      throw error;
    }
  },

  /**
   * Resend verification email
   * @param {string} email - User's email address
   * @returns {Promise<Object>} Response with success message
   */
  resendVerification: async (email) => {
    try {
      const response = await api.post('/auth/resend-verification', { email });
      return {
        success: true,
        message: extractMessage(response)
      };
    } catch (error) {
      console.error('[AuthService] Resend verification error:', error);
      throw error;
    }
  }
};

export default authService;
